import { useState, useRef, useEffect, useMemo } from 'react';
import {
  ChakraFlex,
  ChakraBox,
  TextInputV2,
  FireflyButton,
  FAIconName,
} from '@equip.health/ui';
import { debounce } from 'lodash';
import DocumentsFilterSelect from './DocumentsFilterSelect';
import {
  PatientDocumentsFilters,
  useGetPatientDocumentProvidersQuery,
} from '~/store/services/documents';
import { useGetPatientDocumentTagsQuery } from '~/store/services/enumerations';
import { PATIENT_DOCUMENT_FILE_FORMAT_FILTER_OPTIONS } from '~/lib/constants/documentCenter.constants';
import { getPatientDocumentUploadedByProviderOption } from '~/lib/util/patientDocuments.util';

import UploadDateFilter from './UploadDateFilter';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface DocumentsSearchProps {
  id: string;
  onSearch: (value: string) => void;
  value?: string;
  patientId: string;
  filters?: PatientDocumentsFilters;
  onFiltersChange?: (filters: PatientDocumentsFilters) => void;
}

const DEBOUNCE_DELAY = 500;

const DocumentsFilter = ({
  onSearch,
  id,
  value,
  patientId,
  filters = {
    documentTypes: [],
    uploadedBy: [],
    uploadStartDate: '',
    uploadEndDate: '',
    fileFormats: [],
  },
  onFiltersChange,
}: DocumentsSearchProps) => {
  const { trackEvent } = useAnalytics();
  const { data: documentTags } = useGetPatientDocumentTagsQuery();
  const { data: documentProviders } =
    useGetPatientDocumentProvidersQuery(patientId);

  const [inputValue, setInputValue] = useState(value || '');
  const [activeFilters, setActiveFilters] = useState<
    Record<keyof DocumentFilters, boolean>
  >({
    documentTypes: false,
    uploadedBy: false,
    uploadStartDate: false,
    uploadEndDate: false,
    fileFormats: false,
  });

  const debouncedSearch = useRef(
    debounce((v: string) => onSearch(v.trim()), DEBOUNCE_DELAY),
  ).current;
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    setInputValue(value || '');
    debouncedSearch(value || '');
  }, [value, debouncedSearch]);
  const handleSearch = (v: string) => {
    setInputValue(v);
    debouncedSearch(v);
  };
  const documentTagsOptions = [
    ...(documentTags ?? []),
    { label: '(No tag added)', value: 'NO_TAGS' },
  ];
  const uploadedByOptions = useMemo(
    () =>
      documentProviders?.map((provider) =>
        getPatientDocumentUploadedByProviderOption(provider, id),
      ) || [],
    [documentProviders, id],
  );

  const fileFormatOptions = PATIENT_DOCUMENT_FILE_FORMAT_FILTER_OPTIONS;

  const handleResetFilters = () =>
    onFiltersChange?.({
      documentTypes: [],
      uploadedBy: [],
      fileFormats: [],
      uploadStartDate: '',
      uploadEndDate: '',
    });

  const hasFilterApplied =
    filters.documentTypes.length > 0 ||
    filters.uploadedBy.length > 0 ||
    filters.fileFormats.length > 0 ||
    Boolean(filters.uploadStartDate) ||
    Boolean(filters.uploadEndDate);

  return (
    <ChakraFlex
      alignItems={hasFilterApplied ? 'flex-start' : 'center'}
      justifyContent="space-between"
    >
      <ChakraFlex alignItems="center" gap="12px" width="75%" wrap="wrap">
        <DocumentsFilterSelect
          activeFilters={activeFilters}
          filterKey="documentTypes"
          filters={filters}
          id={`${id}__documentTypes-filter`}
          onFiltersChange={onFiltersChange}
          options={documentTagsOptions}
          setActiveFilters={setActiveFilters}
          title="Document type"
        />

        <DocumentsFilterSelect
          activeFilters={activeFilters}
          filterKey="uploadedBy"
          filters={filters}
          id={`${id}__uploadedBy-filter`}
          onFiltersChange={onFiltersChange}
          options={uploadedByOptions}
          setActiveFilters={setActiveFilters}
          title="Uploaded by"
          usePrimaryTextAsLabel
        />

        <UploadDateFilter
          filters={filters}
          id={`${id}__upload-date-filter`}
          onFiltersChange={onFiltersChange}
        />

        <DocumentsFilterSelect
          activeFilters={activeFilters}
          filterKey="fileFormats"
          filters={filters}
          id={`${id}__fileFormats-filter`}
          onFiltersChange={onFiltersChange}
          options={fileFormatOptions}
          setActiveFilters={setActiveFilters}
          title="File format"
        />

        {hasFilterApplied && (
          <FireflyButton
            id={`${id}__reset-filters-button`}
            leftIcon={FAIconName.redoRegular}
            onClick={handleResetFilters}
            size="small"
            variant="secondary"
          >
            Reset filters
          </FireflyButton>
        )}
      </ChakraFlex>

      <ChakraBox width="25%">
        <TextInputV2
          id={`${id}__search-input`}
          leftIcon={FAIconName.searchRegular}
          onChange={handleSearch}
          placeholder="Search documents"
          value={inputValue}
          onFocus={() => {
            trackEvent(DocumentCenterEvents.searchDocumentsFieldClicked, {
              Source: AnalyticsSourceValues.DOCUMENT_CENTER,
            });
          }}
        />
      </ChakraBox>
    </ChakraFlex>
  );
};

export default DocumentsFilter;
